/**
 * Perfil de descarga/decode adaptado a recursos del dispositivo.
 * Señales: CPU (hardwareConcurrency), RAM (deviceMemory), red (Network Information API).
 * La clase UI (phone/tablet) solo acota min/max y throttles de UI, no fija un "6 mágico".
 */
import { isMobile, isTablet } from 'react-device-detect'

/** Viewport compacto / touch: mismo criterio que Explorer y App. */
export function isConstrainedUiDevice() {
  if (typeof window === 'undefined') return false
  return isMobile || isTablet || window.innerWidth < 1024
}

/** Teléfono (no tablet): UI más estrecha / touch phone. */
export function isPhoneLikeDevice() {
  if (typeof window === 'undefined') return false
  const narrow = window.innerWidth > 0 && window.innerWidth < 768
  return (isMobile && !isTablet) || narrow
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n))
}

/**
 * Snapshot de recursos del browser (SSR-safe).
 * @returns {{
 *   hw: number,
 *   deviceMemory: number|null,
 *   effectiveType: string|null,
 *   saveData: boolean,
 *   downlink: number|null,
 *   phone: boolean,
 *   constrained: boolean,
 * }}
 */
export function getResourceHints() {
  if (typeof navigator === 'undefined') {
    return {
      hw: 4,
      deviceMemory: null,
      effectiveType: null,
      saveData: false,
      downlink: null,
      phone: false,
      constrained: false,
    }
  }

  const hw =
    typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency > 0
      ? navigator.hardwareConcurrency
      : 4

  const deviceMemory =
    typeof navigator.deviceMemory === 'number' && navigator.deviceMemory > 0
      ? navigator.deviceMemory
      : null

  const connection =
    navigator.connection || navigator.mozConnection || navigator.webkitConnection || null

  const effectiveType =
    connection && typeof connection.effectiveType === 'string' ? connection.effectiveType : null

  const saveData = !!(connection && connection.saveData)

  const downlink =
    connection && typeof connection.downlink === 'number' && connection.downlink > 0
      ? connection.downlink
      : null

  return {
    hw,
    deviceMemory,
    effectiveType,
    saveData,
    downlink,
    phone: isPhoneLikeDevice(),
    constrained: isConstrainedUiDevice(),
  }
}

/**
 * Concurrencia de descarga target (sin clamp a taskCount).
 * CPU × factor por clase UI → techo RAM → techo red.
 */
export function computeDownloadConcurrencyTarget(hints) {
  const h = hints || getResourceHints()

  // Factor CPU: un poco más agresivo en desktop/tablet con cores de sobra
  let k = 1.5
  if (h.phone) k = 1.1
  else if (h.constrained) k = 1.3

  let raw = Math.ceil(h.hw * k)

  // Bandas por clase de UI (recursos aún pueden bajar esto)
  let minCap = 4
  let maxCap = 20
  if (h.phone) {
    minCap = 3
    maxCap = 10
  } else if (h.constrained) {
    minCap = 4
    maxCap = 12
  }

  raw = clamp(raw, minCap, maxCap)

  // Techos por RAM (deviceMemory en GiB; ausente en muchos Safari/iOS)
  if (h.deviceMemory != null) {
    if (h.deviceMemory <= 2) raw = Math.min(raw, 3)
    else if (h.deviceMemory <= 4) raw = Math.min(raw, 6)
    else if (h.deviceMemory <= 8) raw = Math.min(raw, 10)
    else if (h.deviceMemory <= 16) raw = Math.min(raw, 14)
  } else if (h.phone) {
    // iOS sin deviceMemory: no subir al max de banda solo por UI
    raw = Math.min(raw, Math.max(minCap, Math.ceil(h.hw * 1.25)))
  }

  // Red
  if (h.saveData) {
    raw = Math.min(raw, 2)
  } else if (h.effectiveType === 'slow-2g' || h.effectiveType === '2g') {
    raw = Math.min(raw, 2)
  } else if (h.effectiveType === '3g') {
    raw = Math.min(raw, 4)
  } else if (h.downlink != null && h.downlink < 1.5) {
    raw = Math.min(raw, 4)
  } else if (h.downlink != null && h.downlink < 5) {
    raw = Math.min(raw, Math.max(minCap, Math.ceil(raw * 0.75)))
  }

  return Math.max(1, raw)
}

/**
 * Paralelos de descarga/decode del manifest (concurrency 0 → auto).
 */
export function getManifestDownloadConcurrency(taskCount) {
  const n = Math.max(1, taskCount || 1)
  let target = computeDownloadConcurrencyTarget()
  // Estudios grandes: techo anti-OOM ajustado por clase de dispositivo.
  // Desktop potente: 12 workers permiten priorizar series sin bloquear.
  if (n >= 500) {
    const h = getResourceHints()
    const largeCap = h.phone ? 4 : h.constrained ? 6 : 12
    target = Math.min(target, largeCap)
  }
  logDownloadProfileOnce(target)
  return Math.min(target, n)
}

/**
 * Techo si el caller pide un entero alto a mano.
 * Un poco por encima del target normal, nunca caos en browser mobile.
 */
export function getConcurrencyHardCap() {
  const h = getResourceHints()
  if (h.saveData) return 3
  if (h.effectiveType === 'slow-2g' || h.effectiveType === '2g') return 3
  if (h.phone) return 12
  if (h.constrained) return 16
  return 24
}

/**
 * maxWebWorkers WADO: ~50% de la concurrencia de descarga, acotado por cores.
 * Nunca mayor que download concurrency (evita cola de decode inútil).
 */
export function getWadoMaxWebWorkers() {
  const h = getResourceHints()
  const download = computeDownloadConcurrencyTarget(h)
  // ~media de descargas en vuelo pueden decodificar a la vez
  let workers = Math.round(download * 0.5)
  const workersMax = h.phone ? 6 : h.constrained ? 8 : 12
  workers = clamp(workers, 2, Math.min(workersMax, h.hw, download))
  return workers
}

/** ms entre flushes Redux/files durante descarga R2 (base; loader puede subir en N grande) */
export function getManifestReduxFlushMs(taskCount) {
  const n = taskCount != null ? taskCount : 0
  // Estudios medianos/grandes: menos setState/Explorer → más CPU libre para scroll
  if (n > 400) {
    if (isPhoneLikeDevice()) return 1400
    if (isConstrainedUiDevice()) return 1100
    return 900
  }
  if (n > 150) {
    if (isPhoneLikeDevice()) return 700
    if (isConstrainedUiDevice()) return 550
    return 450
  }
  if (isPhoneLikeDevice()) return 400
  if (isConstrainedUiDevice()) return 280
  return 250
}

/**
 * Intervalo mínimo entre pases de miniaturas (throttle leading+trailing).
 */
export function getExplorerThumbnailThrottleMs() {
  if (isPhoneLikeDevice()) return 700
  if (isConstrainedUiDevice()) return 500
  return 300
}

/**
 * Caché Cornerstone: escala con deviceMemory cuando existe.
 */
export function getCornerstoneImageCacheMaxBytes() {
  const h = getResourceHints()
  const MiB = 1024 * 1024
  if (h.deviceMemory != null) {
    if (h.deviceMemory <= 2) return 32 * MiB
    if (h.deviceMemory <= 4) return 96 * MiB
    if (h.deviceMemory <= 8) return 256 * MiB
    if (h.deviceMemory <= 16) return 512 * MiB
    return 768 * MiB
  }
  if (h.phone) return 48 * MiB
  if (h.constrained) return 128 * MiB
  return 512 * MiB
}

/** Intervalo mínimo entre soft-sync del stack del viewport */
export function getActiveSeriesSyncMinMs() {
  if (isPhoneLikeDevice()) return 450
  if (isConstrainedUiDevice()) return 300
  return 160
}

/** Perfil resumido (debug / telemetría). */
export function describeDownloadProfile() {
  const h = getResourceHints()
  const download = computeDownloadConcurrencyTarget(h)
  return {
    ...h,
    downloadConcurrency: download,
    wadoWorkers: getWadoMaxWebWorkers(),
    imageCacheMiB: Math.round(getCornerstoneImageCacheMaxBytes() / (1024 * 1024)),
    hardCap: getConcurrencyHardCap(),
  }
}

function logDownloadProfileOnce(downloadConcurrency) {
  if (typeof window === 'undefined') return
  if (window.__udvDownloadProfileLogged) return
  window.__udvDownloadProfileLogged = true
  try {
    const profile = describeDownloadProfile()
    if (downloadConcurrency != null) {
      profile.downloadConcurrency = downloadConcurrency
    }
    // eslint-disable-next-line no-console
    console.info('[UDV] download profile', profile)
  } catch (e) {
    // ignore
  }
}
